// Copyright (c) Docugami, Inc. All rights reserved.**/

import { IconButton, Callout, TextField, TooltipHost, Label, } from "@fluentui/react";
import { getValidatedLabelErrorMessage } from "components/cards/ReportItemTitleInputField";
import { appContentColor, lightButtonGray } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { uniqueId } from "lodash";
import { ReportEntryType } from "networking/reportsApi";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createOperationId, useAddEntryMutation } from "store/reports/reportsSlice";
import { createSelectEntryTitles, selectIsReportPublished, selectReportId } from "store/reports/selectors";
import styled from "styled-components";
import { AddZeroShotEntryButtonClickedSchema, ZeroShotEntryAddedSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";

const AddItemTextFieldContainer = styled.div`
    width: 350px;
    padding: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 5px;
`;

interface AddItemHeaderProps {
    className?: string;
    entryIndex?: number;
}

const selectEntryTitles = createSelectEntryTitles();

export const AddItemButton = (props: AddItemHeaderProps) => {
    const reportId = useAppSelector(selectReportId);
    const isPublished = useAppSelector(selectIsReportPublished);

    const [showTextField, setShowTextField] = useState(false);

    const buttonId = useMemo(() => uniqueId('addButton'), []);

    const onAddButtonClick = () => {
        setShowTextField(true);
        Telemetry.trackEvent<AddZeroShotEntryButtonClickedSchema>({
            reportId: reportId,
            eventName: TelemetryEvent.AddZeroShotEntryButtonClicked
        });
    };

    if (isPublished) {
        return null;
    }

    return (
        <div className={props.className}>
            <TooltipHost content={'Add a new item'}>
                <IconButton
                    id={buttonId}
                    iconProps={{
                        iconName: 'Add'
                    }}
                    onClick={onAddButtonClick}
                    styles={{
                        root: {
                            border: 'none',
                            fontWeight: 600,
                            color: appContentColor,
                            background: lightButtonGray,
                            borderRadius: 16
                        }
                    }}
                />
            </TooltipHost>
            <AddZeroShotEntryCallout
                reportId={reportId}
                entryIndex={props.entryIndex || 0}
                targetElementId={buttonId}
                hidden={!showTextField}
                onDismiss={() => setShowTextField(false)} />
        </div>
    )
};

// This allows the add button to hover to the right of an element
export const RightOverlayedAddItemButton = styled(AddItemButton)`
    position: absolute;
    // 16px is half the button size to have it strattling the edge of the parent element
    top: calc(50% - 16px);
    left: calc(100% - 32px);

    opacity: 1;
`;

interface AddZeroShotEntryCalloutProps {
    reportId: string;
    entryIndex: number;
    targetElementId: string;
    hidden: boolean;
    onDismiss: () => void;
    groupId?: string;
}

export const AddZeroShotEntryCallout = (props: AddZeroShotEntryCalloutProps) => {
    const textFieldId = 'newItemLabel';

    const [label, setLabel] = useState<string | null>(null);

    const [errorMessage, setErrorMessage] = useState('');

    const existingEntryTitles = useAppSelector(state => selectEntryTitles(state, props.reportId));

    const [trigger, status] = useAddEntryMutation();

    useEffect(() => {
        if (label !== null) {
            setErrorMessage(getValidatedLabelErrorMessage(label, existingEntryTitles))
        }
    }, [existingEntryTitles, label]);

    const onAddEntry = () => {
        if (!errorMessage && !!label && !status.isLoading) {
            dismiss();

            trigger({
                reportId: props.reportId,
                definition: {
                    title: label,
                    entryType: ReportEntryType.Text,
                    index: props.entryIndex || 0,
                    groupId: props.groupId
                },
                operationId: createOperationId()
            });

            Telemetry.trackEvent<ZeroShotEntryAddedSchema>({
                reportId: props.reportId,
                label: label,
                eventName: TelemetryEvent.ZeroShotEntryAdded
            });
        }
    };

    const dismiss = useCallback(() => {
        props.onDismiss();
        setErrorMessage('');
        setLabel(null);
    }, [props]);

    return (
        <Callout
            styles={{
                root: {
                    minHeight: 85
                }
            }}
            hidden={props.hidden || !props.targetElementId}
            role="dialog"
            target={`#${props.targetElementId}`}
            onDismiss={dismiss}
            setInitialFocus
        >
            <AddItemTextFieldContainer>
                <Label htmlFor={textFieldId} >
                    Enter a label to add a new item
                </Label>
                <TextField
                    id={textFieldId}
                    value={label ?? ''}
                    onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                            event.stopPropagation();
                            onAddEntry();
                        }
                    }}
                    autoFocus={true}
                    onChange={(event, newValue) => setLabel(newValue || '')}
                    placeholder='Enter label here...'
                    iconProps={{
                        iconName: "ReturnKey",
                        style: {
                            pointerEvents: "auto",
                            cursor: "pointer",
                            zIndex: 999
                        },
                        styles: {
                            root: {
                                opacity: .5,
                                ':hover': {
                                    opacity: 1,
                                }
                            }
                        },
                        onMouseDown: (event) => {
                            event.stopPropagation();
                            onAddEntry();
                        }
                    }}
                    styles={{
                        root: {
                            width: 328
                        }
                    }}
                    errorMessage={errorMessage}
                />
            </AddItemTextFieldContainer>
        </Callout>
    );
};

